/**
 * The small parts of the live room, as the confirmed prototype draws them (g-front, the room before and during the
 * live): a face's picture, a seat in the row, the quick reactions, the lane they float up, and the words a face's
 * state is read out in. The meeting itself is today's (`src/live/meeting/meeting.ts`); these only draw it.
 */
import { memo, type ReactNode, type RefObject, useEffect, useLayoutEffect, useRef, useState } from "react";
import { REACTIONS, type ReactionKind } from "../../../shared/live-reactions";
import type { Meeting, MeetingFace } from "../../live/meeting/meeting";
import { STATS_ON } from "../../live/meeting/stats";
import { TileStats } from "../../live/meeting/TileStats";
import { useTileWidth } from "../../live/meeting/useTileWidth";
import { initialsOf } from "../../live/meeting/table";
import { FloatLane } from "../../live/room/FloatLane";
import type { Lane } from "../../live/room/lane";
import { GIcon } from "../room/GIcon";

/** The first word of a name, as a seat and the chat say it. */
export function firstOf(name: string): string {
  return name.trim().split(/\s+/)[0] ?? name;
}

/** One person's picture, from the meeting's track; nothing when there is none. */
export function TrackVideo({
  track,
  mirror,
  contain,
  label,
  meeting,
  peer,
  mine,
}: {
  track: MediaStreamTrack | null;
  /** With `peer`, tells the meeting how wide this tile is, so a small one is sent a small picture. */
  meeting?: Meeting;
  peer?: string;
  /** Your own camera: nothing is asked for it, and its measuring line is what you send. */
  mine?: boolean;
  /** Your own camera reads as a mirror, the way people expect to see themselves. */
  mirror?: boolean;
  /** A shared screen is shown whole, at its own shape, never cropped. */
  contain?: boolean;
  label?: string;
}): ReactNode {
  const video = useRef<HTMLVideoElement>(null);
  useEffect(() => {
    const el = video.current;
    if (!el) return;
    el.srcObject = track ? new MediaStream([track]) : null;
    if (track) void el.play().catch(() => {});
  }, [track]);
  useTileWidth(mine ? undefined : meeting, peer, video);
  const style = mirror || contain ? { ...(mirror ? { transform: "scaleX(-1)" } : {}), ...(contain ? { objectFit: "contain" as const } : {}) } : undefined;
  return (
    <>
      <video ref={video} muted playsInline aria-label={label} style={style} />
      {STATS_ON ? <TileStats track={track} mine={!!mine} /> : null}
    </>
  );
}

/** Hand up, number N, as a seat carries it. */
function HandMark({ place }: { place: number }): ReactNode {
  return (
    <span className="hnd" title={`Hand up, number ${place}`}>
      <GIcon name="hand" size={12} stroke={2.2} />
      {place}
    </span>
  );
}

/** What a face is doing, in the words a seat and the people list read out. */
export function faceWords(face: Pick<MeetingFace, "micOn" | "cameraOn">): string {
  return `${face.micOn ? "Mic on" : "Muted"} · ${face.cameraOn ? "camera on" : "camera off"}`;
}

/** One seat in the row under the stage: a face, or your own with "You". */
export const Seat = memo(function Seat({
  name,
  you,
  photo,
  track,
  cameraOn,
  micOn,
  speaking,
  arriving,
  place,
  reconnecting,
  mirror,
  onExpand,
  column,
  meeting,
  peer,
}: {
  name: string;
  you?: boolean;
  /** The meeting and this person's id in it, so their tile can say how wide it is. */
  meeting?: Meeting;
  peer?: string;
  /** Their own photo, shown while there is no camera to show; initials without one. */
  photo?: string | null;
  /** Their picture; left out, the seat asks the meeting for it itself, so only a seat that is drawn asks. */
  track?: MediaStreamTrack | null;
  cameraOn: boolean;
  micOn: boolean;
  speaking?: boolean;
  arriving?: boolean;
  /** Their place in the line of hands, or zero. */
  place: number;
  reconnecting?: boolean;
  mirror?: boolean;
  /** Stable for the whole row: it is told which seat was pressed. */
  onExpand?: (peer?: string) => void;
  /** The grid column this seat starts in, when the Everyone view centres a short row. */
  column?: number;
}): ReactNode {
  const cls = ["seat", you ? "you" : "", speaking ? "speak" : "", arriving ? "arrive" : "", you && reconnecting ? "reco" : ""]
    .filter(Boolean)
    .join(" ");
  const picture = track === undefined ? (cameraOn && meeting && peer ? meeting.trackOf(peer, "video") : null) : track;
  const label = `${name}${you ? ", you" : ""}${micOn ? "" : ", muted"}${cameraOn ? "" : ", camera off"}. Expand`;
  return (
    <button className={cls} type="button" title={you ? `${name} (you)` : name} aria-label={label} onClick={onExpand ? () => onExpand(peer) : undefined} style={column ? { gridColumnStart: column } : undefined}>
      {cameraOn && picture ? (
        <TrackVideo track={picture} mirror={mirror} meeting={meeting} peer={peer} mine={you} />
      ) : photo ? (
        <img src={photo} alt="" style={you ? { filter: "brightness(.7)" } : undefined} />
      ) : (
        <span className="ini">{initialsOf(name)}</span>
      )}
      {you ? <span className="you-tag">You</span> : <span className="seat-n">{firstOf(name)}</span>}
      {!micOn && !(you && reconnecting) ? (
        <span className="mo-off">
          <GIcon name="micoff" size={12} stroke={2.2} />
        </span>
      ) : null}
      {place > 0 ? <HandMark place={place} /> : null}
    </button>
  );
});

/** The seven reactions, one tap each: in the dock's line, or in the pill the React button opens. */
export function QuickReactions({ onTap, off }: { onTap: (kind: ReactionKind) => void; off?: boolean }): ReactNode {
  const popped = useRef<HTMLButtonElement | null>(null);
  return (
    <div className="qreact" role="group" aria-label="React" aria-disabled={off || undefined}>
      {REACTIONS.map((r) => (
        <button
          key={r.kind}
          className="qr"
          type="button"
          aria-label={r.label}
          title={r.label}
          onClick={(e) => {
            const b = e.currentTarget;
            popped.current?.classList.remove("pop");
            b.classList.remove("pop");
            void b.offsetWidth;
            b.classList.add("pop");
            popped.current = b;
            onTap(r.kind);
          }}
        >
          {r.emoji}
        </button>
      ))}
    </div>
  );
}

// Why: the group keeps this much clear of the views on the left and Leave on the right.
const DOCK_AIR = 16;

/**
 * Whether the seven reactions fit on the dock's line. It is a pure function of measured widths, so a width that
 * folds never unfolds on the next pass: the folded button is measured as the pill it stands for.
 */
export function useReactionsFit(dock: RefObject<HTMLElement | null>, group: RefObject<HTMLElement | null>, tight: boolean, centred: boolean, again: readonly unknown[]): boolean {
  const [fits, setFits] = useState(!tight);
  const pillWidth = useRef(0);
  useLayoutEffect(() => {
    const d = dock.current;
    const g = group.current;
    if (!d || !g) return;
    const decide = () => {
      if (tight) {
        setFits(false);
        return;
      }
      const pill = g.querySelector<HTMLElement>(".qreact");
      if (pill) pillWidth.current = pill.offsetWidth;
      // Why: the pill's width is learned the first time it is drawn, so draw it before judging.
      if (!pillWidth.current) {
        setFits(true);
        return;
      }
      const fold = g.querySelector<HTMLElement>(".db.react");
      const need = g.offsetWidth - (fold ? fold.offsetWidth - pillWidth.current : 0);
      const box = d.getBoundingClientRect();
      const style = getComputedStyle(d);
      const leave = d.querySelector<HTMLElement>(".db.leave")?.getBoundingClientRect();
      const views = d.querySelector<HTMLElement>(".db-l");
      let room = 0;
      if (centred) {
        const mid = box.left + box.width / 2;
        const left = views && views.offsetWidth > 0 ? views.getBoundingClientRect().right : box.left + Number.parseFloat(style.paddingLeft);
        room = 2 * Math.min(mid - left, (leave?.left ?? box.right) - mid);
      } else {
        room = box.width - Number.parseFloat(style.paddingLeft) - Number.parseFloat(style.paddingRight) - (leave ? leave.width + Number.parseFloat(style.columnGap || "0") : 0);
      }
      setFits(need <= room - 2 * DOCK_AIR);
    };
    decide();
    const watch = new ResizeObserver(decide);
    watch.observe(d);
    watch.observe(g);
    for (const el of d.querySelectorAll(".db-l, .db.leave")) watch.observe(el);
    return () => watch.disconnect();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dock, group, tight, centred, ...again]);
  return fits;
}

/** The lane the room's reactions float up, on the right of the picture. */
export function Floats({ lane, cap }: { lane: RefObject<Lane | null>; cap: number }): ReactNode {
  return (
    <div className="floats">
      <FloatLane lane={lane} cap={cap} className="fl-lane" />
    </div>
  );
}
